import { navigateToAndResetPageAndSearch } from 'front-end/src/pages/analytics/helpers'
import React, { FC, useContext, useState } from 'react'
import { Column } from 'react-table'
import { t } from '@lingui/macro'
import { SqlOrderDirectionEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import { DownloadMediaCSV } from './DownloadMediaCSV'
import { getPagesCount } from '../utils'
import { linkBuilder } from 'shared/linkBuilder'
import { MediaOverviewOrderColumn } from 'types/AnalyticsMedia'
import { useMediaAnalyticsOverviewQuery } from './Media.codegen'
import {
  formatDateAndTimeShort,
  formatDateShort
} from '../../../utils/dateHelpers'
import { Box } from '@chakra-ui/react'
import { AnalyticsMediaReportModel } from '../../../graphql/private/PrivateSchemaTypes'
import { AnalyticsContext } from '../AnalyticsContext'
import { StatusArchivedGrey } from 'front-end/src/components/common/status/Status'

export const MediaOverviewTable: FC<{}> = () => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [overviewTableFilter, setOverviewTableFilters] = useState({
    orderColumn: 'avgEngagement' as any,
    orderDirection: SqlOrderDirectionEnum.DESC,
    limit: 20
  })

  const overviewTableQuery = {
    ...overviewTableFilter,
    page: filter.page,
    search: filter.search
  }

  const { data, loading } = useMediaAnalyticsOverviewQuery({
    fetchPolicy: 'no-cache',
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      mediaOverviewFilter: overviewTableQuery
    }
  })

  const tableColumns: Array<Column<AnalyticsMediaReportModel>> = [
    {
      Header: t`File name`,
      accessor: 'fileName',
      className: 'overflowVisible',
      Cell: (row) => (
        <Box className="overflowEllipsis">
          {row.value}
          {row.original.deletedAt && <StatusArchivedGrey />}
        </Box>
      )
    },
    {
      Header: t`Resource name`,
      maxWidth: 200,
      accessor: 'resourceName',
      Cell: (row) => (
        <Box className="overflowEllipsis">
          <strong className="dark">{row.value}</strong>
          {row.original.resourceDeletedAt && <StatusArchivedGrey />}
        </Box>
      )
    },
    {
      Header: t`Uploaded at`,
      maxWidth: 175,
      accessor: 'createdAt',
      Cell: (row) => formatDateAndTimeShort(row.value)
    },
    {
      Header: t`Avg. Engagement`,
      maxWidth: 150,
      accessor: 'avgEngagement',
      Cell: (row) => `${row.value}%`
    },
    {
      Header: t`Plays`,
      maxWidth: 90,
      accessor: 'playsCount'
    },
    {
      Header: t`Impressions`,
      maxWidth: 150,
      accessor: 'impressionsCount'
    },
    {
      Header: t`Play rate`,
      maxWidth: 100,
      accessor: 'playRate',
      Cell: (row) => `${row.value}%`
    },
    {
      Header: t`Last activity`,
      maxWidth: 100,
      accessor: 'lastActivity',
      Cell: (row) => formatDateShort(row.value)
    }
  ]

  let pagesCount = 1
  if (data && data.analytics) {
    pagesCount = getPagesCount(
      data.analytics.media.mediaOverview?.count || 0,
      overviewTableQuery.limit
    )
  }

  return (
    <div>
      <TableExtendedOld
        loading={loading}
        manual
        data={
          data && data.analytics && data.analytics.media.mediaOverview?.items
        }
        tableHeader={t`Media`}
        columns={tableColumns}
        className="-highlight AnalyticsTable"
        defaultPageSize={overviewTableQuery.limit}
        showPageSizeOptions={false}
        showSearchFilter
        resizable={false}
        minRows={5}
        pages={pagesCount}
        defaultSortDesc
        getTrProps={(_state, rowInfo) => ({
          onClick: () =>
            navigateToAndResetPageAndSearch(
              linkBuilder.analyticsMediaDetail(rowInfo?.row._original.id),
              changeFilter
            ),
          className: `clickable ${
            rowInfo &&
            (rowInfo?.row._original.deletedAt ||
              rowInfo?.row._original.resourceDeletedAt)
              ? 'archived'
              : ''
          }`,
          ...DEFAULT_TR_PROPS
        })}
        defaultSorted={[
          {
            id: overviewTableQuery.orderColumn,
            desc:
              overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setOverviewTableFilters((prevState) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false

            return {
              ...prevState,
              orderColumn: newSorted[0]
                ? (newSorted[0].id as MediaOverviewOrderColumn)
                : MediaOverviewOrderColumn.avgEngagement,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            }
          })
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={overviewTableQuery.search}
        page={overviewTableQuery.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
        paginationContentAfter={
          data && (
            <DownloadMediaCSV
              title={`${t`Export data`} (.csv)`}
              mediaOverviewFilter={overviewTableQuery}
              mediaCount={data.analytics.media.mediaOverview?.count || 0}
            />
          )
        }
      />
    </div>
  )
}
